import type { Breadcrumb, BreadcrumbHint, ErrorEvent, EventHint } from '@sentry/core';
import * as Sentry from '@sentry/react';

import * as env from 'src/lib/env';
import * as gtm from 'src/lib/gtm';
import * as logRocket from 'src/lib/logrocket';
import * as storage from 'src/lib/storage';

import { prepareDenyUrls, prepareIgnoreErrors, shouldSentryEventBeSend } from './filters';
import { transformBreadcrumbBeforeSend, transformEventBeforeSend } from './transforms';
import { getEventTitle, SentryTestException } from './utils';

export { sentryClearUser, sentrySetUser } from './utils';

const ALM_DEBUG_FLAG = 'ALM_DEBUG_FLAG';

let initialized = false;

const checkDsn = (): boolean => !!env.getSentryDSN();

const reportEventToGTM = (event: ErrorEvent): void => {
  gtm.pushDataLayerEvent(gtm.EVENTS.systemSentryEvent, {
    eventId: event.event_id,
    level: event.level,
    message: getEventTitle(event),
  });
};

const reportEventToLogRocket = (event: ErrorEvent): void => {
  logRocket.captureError(getEventTitle(event), {
    eventId: event.event_id || '',
    level: event.level || '',
  });
};

export const init = (): void => {
  if (!checkDsn()) {
    return;
  }

  try {
    Sentry.init({
      dsn: env.getSentryDSN(),
      environment: env.getAppEnv(),
      release: `${env.getRepositoryTag()}`,
      integrations: [Sentry.browserTracingIntegration()],

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: 0,

      ignoreErrors: prepareIgnoreErrors(),
      denyUrls: prepareDenyUrls(),

      /*
        Can use that to modify the event's data or drop it completely (by returning null)
        https://docs.sentry.io/platforms/javascript/guides/react/configuration/filtering/
       */
      beforeSend: (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
        // setTimeout to add this low priority task to the end of the thread
        setTimeout(() => {
          const flag = storage.readItem(ALM_DEBUG_FLAG);
          if (flag) {
            // eslint-disable-next-line no-console
            console.log('ALM_DEBUG_FLAG.event', event);
            // eslint-disable-next-line no-console
            console.log('ALM_DEBUG_FLAG.hint', hint);
          }
        });

        // Should we drop this event
        if (!shouldSentryEventBeSend(event, hint)) {
          return null;
        }

        // Transform event if needed and return it
        const transformed = transformEventBeforeSend(event, hint);

        // Report event to GTM and LogRocket before sending it
        reportEventToGTM(transformed);
        reportEventToLogRocket(transformed);

        return transformed;
      },

      beforeBreadcrumb: (breadcrumb: Breadcrumb, hint?: BreadcrumbHint): Breadcrumb | null => {
        return transformBreadcrumbBeforeSend(breadcrumb, hint);
      },
    });

    if (env.getRepositoryTag()) {
      Sentry.getCurrentScope().setTag('version', env.getRepositoryTag());
    }
    if (env.getCommitHash()) {
      Sentry.getCurrentScope().setTag('commit', env.getCommitHash());
    }

    initialized = true;
  } catch (e) {
    if (env.getAppEnv() === 'development') {
      // eslint-disable-next-line no-console
      console.warn('Error in sentry init', e);
    }
    initialized = false;
  }
};

export const captureException = (error: Error, options?: Record<string, unknown>): void => {
  if (!checkDsn() || !initialized) {
    // Redirect everything to console in 'development' environment
    // eslint-disable-next-line no-console
    console.error(error, options);
    return;
  }

  Sentry.captureException(error, options);
};

export const captureMessage = (message: string, options?: Record<string, unknown>): void => {
  const alignedOptions = options || {};
  if (!alignedOptions.level) {
    alignedOptions.level = 'info';
  }

  if (!checkDsn() || !initialized) {
    // Redirect everything to console in 'development' environment
    // eslint-disable-next-line no-console
    console.log(message, alignedOptions);
    return;
  }

  Sentry.captureMessage(message, alignedOptions);
};

export const showReportDialog = (): void => {
  if (!checkDsn() || !initialized) {
    return;
  }

  Sentry.showReportDialog();
};

window.sentryTest = (message: string, attributes?: Record<string, unknown>): void => {
  captureMessage(message || 'Test message xxx', attributes);
};

window.sentryTestException = (message: string, attributes?: Record<string, unknown>): void => {
  captureException(new SentryTestException(message || 'Test exception'), attributes);
};
